import PropTypes from 'prop-types';
import React, {useContext, useEffect, useRef, useState} from 'react';
import Modal from 'react-modal';
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import styled from 'styled-components';
import useFunctionKeys from '../hooks/useFunctionKeys';
import ApplicationContext from '../ApplicationContext';

const ModalBase = styled.div`
  .ifms-modal-header {
    display: flex;
    align-items: center;
    overflow: hidden;
    
    h1 {
      display: flex;
      align-items: center;
      justify-content: center;

      font-style: normal;
      font-weight: bold;
      height: 100%;
      // line-height: 20px;
      // display: inline-block;
      margin: 0 !important;
    }

    .ifms-modal-right {
       display: flex;
      align-items: center;
      margin-left: auto;
    }

    .ifms-modal-close {
      display: flex;
      align-items: center;
      justify-content: center;

      font-style: normal;
      font-weight: bold;
      // line-height: 20px;
      height: 60%;
      width: 30px;
      //margin-left: auto;
      cursor: pointer;
    }

    .ifms-modal-close-before {
      display: flex;
      align-items: center;
      margin-right: 8px;
    }
  }

  .ifms-modal-content {
    padding: 20px;
  }

  .ifms-modal-content-nopadding {
    padding: 0px;
    overflow: hidden;
    position: relative;
    left: -16px;
  }

  .ifms-modal-header-draggable {
    cursor: all-scroll;
  }
`;

const DefaultModalContainer = styled(ModalBase)`
  padding: 16px;

  .ifms-modal-header {
    justify-content: center;
    border-bottom: 1px solid #cfcfcf;
    padding-bottom: 12px;
    margin-left: 16px;
    margin-right: 16px;

    h1 {
      font-family: Arial;
      font-size: 14px;
    }

    .ifms-modal-close {
      font-family: Arial;
      font-size: 14px;
    }
  }
`;

const BlueModalContainer = styled(ModalBase)`
  .ifms-modal-header {
    padding: 0 40px;
    background: #0079a4;
    color: white;
    height: 50px;

    h1 {
      font-family: Arial;
      font-size: 12px;
    }

    .ifms-modal-close {
      font-family: Arial;
      font-size: 14px;
    }
  }
`;

const DarkModeModalContainer = styled(ModalBase)`
  .ifms-modal-header {
    padding: 0 40px;
    background: #575B5E;
    color: white;
    height: 50px;

    h1 {
      font-family: Arial;
      font-size: 12px;
    }

    .ifms-modal-close {
      font-family: Arial;
      font-size: 14px;
    }
  }
`;


const BlueModalSmallContainer = styled(ModalBase)`
  .ifms-modal-header {
    padding: 0 40px;
    background: #0079a4;
    color: white;
    height: 30px;

    h1 {
      font-family: Arial;
      font-size: 12px;
    }

    .ifms-modal-close {
      font-family: Arial;
      font-size: 14px;
    }
  }
`;


const DarkModeModalSmallContainer = styled(ModalBase)`
  .ifms-modal-header {
    padding: 0 40px;
    background: #575B5E;
    color: white;
    height: 30px;

    h1 {
      font-family: Arial;
      font-size: 12px;
    }

    .ifms-modal-close {
      font-family: Arial;
      font-size: 14px;
    }
  }
`;


const ModalContentStyle:React.CSSProperties = {
  top: '150px',
  // left: '50%',
  right: 'auto',
  bottom: 'auto',
  // marginRight: '-50%',
  // transform: 'translate(-50%, -50%)',
  padding: 0,
  zIndex: 10000,
  border: 'none',
  overflow:'hidden',
};
const ModalOverlayStyle:React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 888890
};
const SearchModalOverlayStyle:React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 888890 /* Tooltip is not visible in Search Modal */
};

interface Coord {
  x: number|null,
  y: number|null,
};

interface MovedCoord {
  left: string|null,
  top: string|null,
};

const lastPosition:Coord = {
  x: null,
  y: null
};

function IfmsModal({
  isOpen,
  title = undefined,
  onCancel = undefined,
  width = undefined,
  height = undefined,
  top = undefined,
  left = undefined,
  children = undefined,
  onShow = () => {},
  onHide = () => {},
  onAfterOpen = () => {},
  draggable = false,
  noPadding = false,
  isBlueHeader = false,
  noVerticalScroll = false,
  cancelOnEsc = false,
  columnOptionCloseOnEsc = false,
  memoCloseOnEsc = false,
  isSearch = false,
  isSmallHeader = false,
  onPositionChange = undefined,
  beforeClose = undefined
}: {
  isOpen: boolean,
  title?: React.ReactNode|string|undefined,
  onCancel?: ((x:React.MouseEvent|string) => void)|undefined,
  width?: string|undefined,
  height?: string|undefined,
  top?: string|undefined,
  left?: string|undefined,
  children?: React.ReactChild|React.ReactNode|string|null|undefined,
  onShow?: () => void,
  onHide?: () => void,
  onAfterOpen?: () => void,
  draggable? : boolean,
  noPadding? : boolean,
  isBlueHeader? : boolean,
  noVerticalScroll? : boolean,
  cancelOnEsc?: boolean,
  columnOptionCloseOnEsc?: boolean,
  memoCloseOnEsc?: boolean,
  isSearch?: boolean,
  isSmallHeader?: boolean,
  onPositionChange?: (position: { top: number, left: number }) => void;
  beforeClose?: React.ReactNode|undefined,
}) {
  useFunctionKeys({onEsc: isOpen ? onEscape: undefined});
  //useFunctionKeys({onEsc: onEscape});
  const appContext = useContext(ApplicationContext);
  
  const [toggle, setToggle] = useState(isOpen);
  const [movedCoord, setMovedCoord] = useState<MovedCoord|null>(null);

  const dragRef = useRef(false);
  const modalRef = useRef(null);

  const widthStyle = !!width ? { width } : { width: '1320px'};
  const widthNumber = Number(widthStyle.width.replace('px', ''));

  useEffect(() => {
    document.addEventListener('mouseup', stopDrag);
    document.addEventListener('mousemove', handleDrag);
    return () => {
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('mousemove', handleDrag);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (toggle !== isOpen) {
      setToggle(isOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (toggle) {
      if (onShow) {
        onShow();
      }
    } else {
      setMovedCoord(null);
      
      if (onHide) {
        onHide();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle]);

  function onEscape() {
    const modalName = (document.getElementsByClassName('ifms-modal-header-h')[0] as HTMLElement)?.textContent;
    const onCloseColumnOption = modalName === "Column Option"; // ListView > ColumnOption
    const onCloseMemoPopup = modalName === "Task / Memo"; //ListView > Memo & Log List > Memo Popup
    if ((onCloseColumnOption && columnOptionCloseOnEsc) && onCancel) {
      onCancel('esc');
      return true;
    }
    else if ((onCloseMemoPopup && memoCloseOnEsc) && onCancel) {
      onCancel('esc');
      return true;
    }
    else if (cancelOnEsc && onCancel) {
      onCancel('esc');
      return true;
    }
    return false;
  }

  function handleDrag(e:MouseEvent) {
    if (!dragRef.current) {
      return;
    }

    if (!modalRef || !modalRef.current) {
      return;
    }
    const modal = modalRef.current as HTMLElement;
    if (!modal) {
      return;
    }

    const node = modal.parentNode as HTMLElement;
    const rect = node.getBoundingClientRect();

    let lastX = lastPosition.x || e.clientX;
    let lastY = lastPosition.y || e.clientY;

    const deltaX = lastX - e.clientX;
    const deltaY = lastY - e.clientY;

    node.style.left = `${rect.left - deltaX}px`;
    node.style.top = `${rect.top - deltaY}px`;

    lastPosition.x = e.clientX;
    lastPosition.y = e.clientY;
  }

  function stopDrag() {
    dragRef.current = false;

    if (modalRef && modalRef.current) {
      const modal = modalRef.current as HTMLElement;
      if (modal) {
        const node = modal.parentNode as HTMLElement;
        setMovedCoord({
          left: node.style.left,
          top: node.style.top,
        });
        if(onPositionChange){
          let nodeTop = node.style.top;
          let top = parseInt(nodeTop.slice(0,-2));
          let nodeLeft = node.style.left;
          let left = parseInt(nodeLeft.slice(0,-2));
          onPositionChange({top: top, left: left});
        }

      }
    }

    lastPosition.x = null;
    lastPosition.y = null;
  }

  function handleDragStart(e:React.MouseEvent<HTMLDivElement>) {
    if (draggable && !dragRef.current)  {
      dragRef.current = true;
      lastPosition.x = e.clientX;
      lastPosition.y = e.clientY;
    }
  }

  const headerBaseClass = draggable ? 'ifms-modal-header ifms-modal-header-draggable' : 'ifms-modal-header';

  const modalContentStyle = {
    ...ModalContentStyle,
    left: !!left ? left : `${window.innerWidth / 2 - (widthNumber / 2)}px`,
    height,
  };
  if (top) {
    modalContentStyle.top = top;
  }

  if (!!movedCoord) {
    if (!!movedCoord.left) {
      modalContentStyle.left = movedCoord.left;
    }
    if (!!movedCoord.top) {
      modalContentStyle.top = movedCoord.top;
    }
  }

  if (noVerticalScroll) {
    modalContentStyle.overflowY = 'hidden';
  }

  switch (appContext.backgroundColor) {
    case 'A':
      modalContentStyle.background = '#E5FFE5';
      break;
    case 'I':
      modalContentStyle.background = '#FFFFE3';
      break;
    case 'W':
      modalContentStyle.background = 'white';
      break;
  }  
  const ModalContainer = appContext.useDarkMode ?  (isSmallHeader ?  DarkModeModalSmallContainer : DarkModeModalContainer) : isBlueHeader ? (isSmallHeader ?  BlueModalSmallContainer : BlueModalContainer) : DefaultModalContainer ;

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      contentLabel=""
      onAfterOpen={onAfterOpen}
      style={{
        overlay: isSearch ? SearchModalOverlayStyle : ModalOverlayStyle,
        content: modalContentStyle,
      }}
    >
      <ModalContainer ref={modalRef}>
        <div id='ifms-modal-width-element' style={widthStyle}>
          <div className={headerBaseClass} onMouseDown={handleDragStart}>
            <h1 id={`ifms-modal-title-${title}`} className="ifms-modal-header-h">{title}</h1>
            <span className="ifms-modal-right">
              {beforeClose && <span className="ifms-modal-close-before">{beforeClose}</span>}
              <span className="ifms-modal-close" onClick={onCancel}>
                <FontAwesomeIcon id='ifms-modal-close-x-icon' icon={faTimes} />
              </span>
            </span>
          </div>

          <div className={noPadding ? 'ifms-modal-content-nopadding' : 'ifms-modal-content'}>
            {children}
            <div className="clearfix"></div>
          </div>
        </div>
      </ModalContainer>
    </Modal>
  );
}

IfmsModal.defaultProps = {
  onCancel: () => {},
  isOpen: false,
  draggable: false,
  noPadding: false,
  isBlueHeader: false,
  noVerticalScroll: false,
  isSearch: false,
  isSmallHeader: false,
};

IfmsModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  draggable: PropTypes.bool.isRequired,
  noPadding: PropTypes.bool.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  width: PropTypes.string,
  height: PropTypes.string,
  top: PropTypes.string,
  left: PropTypes.string,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  onAfterOpen: PropTypes.func,
  isBlueHeader: PropTypes.bool.isRequired,
  noVerticalScroll: PropTypes.bool.isRequired,
  isSmallHeader : PropTypes.bool.isRequired,
};


export default React.memo(IfmsModal);
